import _styled from "styled-components";
import "core-js/modules/web.dom-collections.iterator.js";
import { action, runInAction } from "mobx";
import { observer } from "mobx-react";
import { useRef, useState } from "react";
import { Trans } from "react-i18next";
import styled, { useTheme } from "styled-components";
import { Category, DataSourceAction } from "../Core/AnalyticEvents/analyticEvents";
import isDefined from "../Core/isDefined";
import Result from "../Core/Result";
import CatalogMemberMixin, { getName } from "../ModelMixins/CatalogMemberMixin";
import MappableMixin from "../ModelMixins/MappableMixin";
import addUserFiles from "../Models/Catalog/addUserFiles";
import Box from "../Styled/Box";
import Icon, { StyledIcon } from "../Styled/Icon";
import Text from "../Styled/Text";
import { raiseFileDragDropEvent } from "../ViewModels/FileDragDropListener";
import { useViewState } from "./Context";
import DragDropNotification from "./DragDropNotification";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const DropZone = styled.div.withConfig({
  displayName: "DragDropFile__DropZone",
  componentId: "sc-z71lk6-0"
})(["position:absolute;background:transparent;opacity:0;z-index:-1;transition:height 0.25s ease-in-out;", ""], _ref => {
  let {
    isActive,
    theme
  } = _ref;
  return isActive && "\n    position: fixed;\n    top: 0;\n    left: 0;\n\n    width: 100vw;\n    height: 100vh;\n    z-index: 99999;\n\n    transition: opacity, 0.2s;\n\n    opacity: 1;\n    display: block;\n    border: 0;\n\n    background: radial-gradient(\n      ellipse at center,\n      ".concat(theme.transparentDark, " 0%,\n      ").concat(theme.dark, " 100%\n    );\n  ");
});
const DragDropFile = observer(() => {
  const viewState = useViewState();
  const theme = useTheme();
  const targetRef = useRef();
  const [lastUploadedFiles, setLastUploadedFiles] = useState([]);
  const handleDrop = async e => {
    e.persist();
    e.preventDefault();
    e.stopPropagation();
    for (let i = 0; i < e.dataTransfer.files.length; i++) {
      var _viewState$terria$ana;
      // Log event to analytics for each file dropped (sometimes multiple files dropped in one DragEvent)
      const fileType = e.dataTransfer.files[i].type || e.dataTransfer.files[i].name.split(".").pop(); // use file extension if type property is empty

      (_viewState$terria$ana = viewState.terria.analytics) === null || _viewState$terria$ana === void 0 || _viewState$terria$ana.logEvent(Category.dataSource, DataSourceAction.addFromDragAndDrop, "File Type: ".concat(fileType, ", File Size(B): ").concat(e.dataTransfer.files[i].size));
    }
    try {
      const addedCatalogItems = await addUserFiles(e.dataTransfer.files, viewState.terria, viewState);
      if (isDefined(addedCatalogItems) && addedCatalogItems.length > 0) {
        runInAction(() => viewState.myDataIsUploadView = false);
        if (viewState.explorerPanelIsVisible) {
          (await viewState.viewCatalogMember(addedCatalogItems[0])).throwIfError("Failed to view ".concat(getName(addedCatalogItems[0])));
          viewState.openUserData();
        } else {
          // update last batch of uploaded files
          setLastUploadedFiles(addedCatalogItems.map(item => CatalogMemberMixin.isMixedInto(item) ? item.name : item.uniqueId));
        }

        // Add load all mapable items
        const mappableItems = addedCatalogItems.filter(MappableMixin.isMixedInto);
        Result.combine(await Promise.all(mappableItems.map(f => f.loadMapItems())), "Failed to load uploaded files").raiseError(viewState.terria);
        raiseFileDragDropEvent({
          addedItems: mappableItems,
          mouseCoordinates: {
            clientX: e.clientX,
            clientY: e.clientY
          }
        });

        // Zoom to first item
        const firstZoomableItem = mappableItems.find(i => isDefined(i.rectangle) && i.disableZoomTo === false);
        if (isDefined(firstZoomableItem)) {
          runInAction(() => viewState.terria.currentViewer.zoomTo(firstZoomableItem, 1));
        }
      }
      runInAction(() => viewState.isDraggingDroppingFile = false);
    } catch (e) {
      viewState.terria.raiseErrorToUser(e, "Failed to upload files");
    }
  };
  const handleDragEnter = action(e => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    targetRef.current = e.target;
  });
  const handleDragOver = e => {
    e.preventDefault();
  };
  const handleDragLeave = action(e => {
    e.preventDefault();
    if (e.screenX === 0 && e.screenY === 0) {
      viewState.isDraggingDroppingFile = false;
    }
    if (e.target === document || e.target === targetRef.current) {
      viewState.isDraggingDroppingFile = false;
    }
  });
  const handleMouseLeave = action(() => {
    viewState.isDraggingDroppingFile = false;
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(DropZone, {
      isActive: viewState.isDraggingDroppingFile,
      onDrop: handleDrop,
      onDragEnter: handleDragEnter,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onMouseLeave: handleMouseLeave,
      children: viewState.isDraggingDroppingFile ? /*#__PURE__*/_jsxs(Box, {
        centered: true,
        fullWidth: true,
        fullHeight: true,
        column: true,
        children: [/*#__PURE__*/_jsx(StyledIcon, {
          glyph: Icon.GLYPHS.dragDrop,
          styledHeight: "80px"
        }), /*#__PURE__*/_jsxs(Trans, {
          i18nKey: "dragDrop.text",
          children: [/*#__PURE__*/_jsx(_StyledText, {
            textLight: true,
            textAlignCenter: true,
            pop: true,
            as: "h3",
            $_css: theme.spacing,
            children: "Drag & Drop"
          }), /*#__PURE__*/_jsx(Text, {
            textLight: true,
            pop: true,
            textAlignCenter: true,
            styledFontSize: "24px",
            children: "Your data anywhere to view on the map"
          })]
        })]
      }) : null
    }), /*#__PURE__*/_jsx(DragDropNotification, {
      uploadedFiles: lastUploadedFiles
    })]
  });
});
DragDropFile.displayName = "DragDropFile";
export default DragDropFile;
var _StyledText = styled(Text).withConfig({
  displayName: "DragDropFile___StyledText",
  componentId: "sc-z71lk6-1"
})(["margin-top:", "px;font-weight:700;"], p => p.$_css);